import { createFileRoute } from '@tanstack/react-router';
import { Card, CardHeader, CardTitle, CardDescription, useUserType } from '@org/ui';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

const BUCKET_BASE_URL = 'https://storage.googleapis.com/media2empower-latam/public/onboard';

type OnboardVideo = {
  id: number;
  filename: string;
  titleKey: string;
  descriptionKey: string;
};

const ONBOARD_VIDEOS: OnboardVideo[] = [
  {
    id: 1,
    filename: '1. Seja bem-vinda a E2E!.mp4',
    titleKey: 'p2e.onboard.videos.welcome.title',
    descriptionKey: 'p2e.onboard.videos.welcome.description',
  },
  {
    id: 2,
    filename: '2. Combinados importantes!.mp4',
    titleKey: 'p2e.onboard.videos.agreements.title',
    descriptionKey: 'p2e.onboard.videos.agreements.description',
  },
  {
    id: 3,
    filename: '3. Aulas Extras da E2E.mp4',
    titleKey: 'p2e.onboard.videos.extraClasses.title',
    descriptionKey: 'p2e.onboard.videos.extraClasses.description',
  },
  {
    id: 4,
    filename: '4. Conheça a Plataforma2Empower.mp4',
    titleKey: 'p2e.onboard.videos.meetPlatform.title',
    descriptionKey: 'p2e.onboard.videos.meetPlatform.description',
  },
  {
    id: 5,
    filename: '5. Tutorial Plataforma 2Empower.mp4',
    titleKey: 'p2e.onboard.videos.tutorial.title',
    descriptionKey: 'p2e.onboard.videos.tutorial.description',
  },
  {
    id: 6,
    filename: '6. Central da Aluna E2E.mp4',
    titleKey: 'p2e.onboard.videos.studentCentral.title',
    descriptionKey: 'p2e.onboard.videos.studentCentral.description',
  },
];

// B2B (in-company) students get their own two videos, kept in a `b2b/`
// subfolder of the same bucket path. Strings live in the p2e namespace.
const B2B_BUCKET_BASE_URL = `${BUCKET_BASE_URL}/b2b`;

const B2B_ONBOARD_VIDEOS: OnboardVideo[] = [
  {
    id: 1,
    filename: '1. Tutorial Plataforma.mp4',
    titleKey: 'p2e:onboardB2b.videos.tutorial.title',
    descriptionKey: 'p2e:onboardB2b.videos.tutorial.description',
  },
  {
    id: 2,
    filename: '2. In-Company Onboarding.mp4',
    titleKey: 'p2e:onboardB2b.videos.inCompany.title',
    descriptionKey: 'p2e:onboardB2b.videos.inCompany.description',
  },
];

export const Route = createFileRoute('/_app/onboard')({
  component: GatedOnboardRoute,
});

/**
 * The six onboarding videos are woman-cohort content (feminine narration and
 * titles); b2b students see their own two in-company videos instead. Same
 * pattern as `FeatureFlagGate`: render nothing while the cohort resolves so
 * neither cohort's videos flash in.
 */
function GatedOnboardRoute() {
  const { isB2b, isLoading } = useUserType();

  if (isLoading) {
    return null;
  }
  if (isB2b) {
    return (
      <OnboardRoute
        baseUrl={B2B_BUCKET_BASE_URL}
        videos={B2B_ONBOARD_VIDEOS}
        headerKeyPrefix="p2e:onboardB2b"
      />
    );
  }
  return <OnboardRoute baseUrl={BUCKET_BASE_URL} videos={ONBOARD_VIDEOS} headerKeyPrefix="p2e.onboard" />;
}

function OnboardRoute({
  baseUrl,
  videos,
  headerKeyPrefix,
}: {
  baseUrl: string;
  videos: OnboardVideo[];
  headerKeyPrefix: string;
}) {
  const { t } = useTranslation('admin');
  const [activeVideo, setActiveVideo] = useState<number | null>(null);

  const getVideoUrl = (filename: string) => {
    return `${baseUrl}/${encodeURIComponent(filename)}`;
  };

  // Media-fragment hint: load just metadata and seek to ~1s so the browser
  // renders that frame as a static poster. No separate image assets needed.
  const getPreviewUrl = (filename: string) => `${getVideoUrl(filename)}#t=1`;

  return (
    <div className="space-y-6 pb-10">
      <header className="space-y-2">
        <p className="text-sm uppercase tracking-[0.3em] text-brand">{t(`${headerKeyPrefix}.eyebrow`)}</p>
        <h1 className="text-3xl font-semibold text-gray-900">{t(`${headerKeyPrefix}.title`)}</h1>
        <p className="text-sm text-gray-600">{t(`${headerKeyPrefix}.description`)}</p>
      </header>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {videos.map((video) => (
          <Card
            key={video.id}
            className="group overflow-hidden rounded-2xl border border-rose-50 bg-white/90 shadow-sm transition hover:-translate-y-0.5 hover:border-rose-300 hover:shadow-md"
          >
            <div className="relative aspect-video w-full overflow-hidden bg-gray-100">
              {activeVideo === video.id ? (
                <video
                  src={getVideoUrl(video.filename)}
                  controls
                  autoPlay
                  className="h-full w-full object-cover"
                  onEnded={() => setActiveVideo(null)}
                >
                  <track kind="captions" />
                </video>
              ) : (
                <button
                  type="button"
                  onClick={() => setActiveVideo(video.id)}
                  className="relative flex h-full w-full items-center justify-center transition focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
                  aria-label={t('p2e.onboard.playVideo')}
                >
                  <video
                    src={getPreviewUrl(video.filename)}
                    preload="metadata"
                    muted
                    playsInline
                    tabIndex={-1}
                    aria-hidden="true"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-brand/10 via-rose-400/10 to-rose-300/10 transition group-hover:from-brand/20 group-hover:via-rose-400/20 group-hover:to-rose-300/20" />
                  <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-brand text-brand-foreground shadow-lg transition group-hover:scale-110">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="ml-1 h-8 w-8"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </button>
              )}
            </div>
            <CardHeader className="p-4">
              <div className="mb-1 flex items-center gap-2">
                <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-900">
                  {t('p2e.onboard.videoLabel', { number: video.id })}
                </span>
              </div>
              <CardTitle as="h2" className="text-lg font-semibold text-gray-900">{t(video.titleKey)}</CardTitle>
              <CardDescription className="text-sm text-gray-600">{t(video.descriptionKey)}</CardDescription>
            </CardHeader>
          </Card>
        ))}
      </div>
    </div>
  );
}
